import { useEffect, useRef } from 'react';

const TRAIN_IMAGE_HEIGHT = 44;

const PASSENGERS = [
  { emoji: '🧑‍💼', xPercent: 10, delay: 0 },
  { emoji: '👩', xPercent: 18, delay: 0.15 },
  { emoji: '👽', xPercent: 28, delay: 0.3 },
  { emoji: '👹', xPercent: 36, delay: 0.1 },
  { emoji: '🧑', xPercent: 44, delay: 0.25 },
  { emoji: '👩‍🦰', xPercent: 54, delay: 0.2 },
  { emoji: '🧓', xPercent: 62, delay: 0.05 },
  { emoji: '🥷', xPercent: 70, delay: 0.35 },
  { emoji: '🤖', xPercent: 78, delay: 0.12 },
  { emoji: '🎅', xPercent: 88, delay: 0.28 },
];

function secureRandom() {
  return crypto.getRandomValues(new Uint32Array(1))[0] / 0xffffffff;
}

type TrainEasterEggEvent = CustomEvent<{
  eenheidNummer: string;
  materieelType: { soort: string; aanduiding: string };
}>;

export default function TrainAnimation() {
  const animatingRef = useRef(false);

  useEffect(() => {
    const handler = (e: Event) => {
      if (animatingRef.current) return;
      animatingRef.current = true;

      const { eenheidNummer, materieelType } = (e as TrainEasterEggEvent).detail;
      const aanduidingBase = materieelType.aanduiding.split('-')[0];
      const baseName = `${materieelType.soort}`.toLowerCase().replaceAll(/\s+/g, '-');
      const trainImage = `${baseName}-${aanduidingBase}`.toLowerCase().replaceAll(/\s+/g, '-');

      const group = document.querySelector(`[data-eenheid="${eenheidNummer}"]`);
      const mwdContainer = document.getElementById('mwdSvgContainer');
      if (!group || !mwdContainer) {
        animatingRef.current = false;
        return;
      }

      const groupRect = group.getBoundingClientRect();
      const containerRect = mwdContainer.getBoundingClientRect();
      const scrollParent = mwdContainer.parentElement;
      const visibleWidth = scrollParent?.clientWidth ?? containerRect.width;
      const scrollLeft = scrollParent?.scrollLeft ?? 0;

      const numericOnly = aanduidingBase.match(/^\d+/)?.[0];
      const fallbacks = [
        numericOnly && numericOnly !== aanduidingBase ? `${baseName}-${numericOnly}` : null,
        baseName !== trainImage ? baseName : null,
      ].filter(Boolean) as string[];

      const img = document.createElement('img');
      img.src = `/assets/treinen/${trainImage}.png`;
      img.onerror = () => {
        const next = fallbacks.shift();
        if (next) {
          img.src = `/assets/treinen/${next}.png`;
        } else {
          img.remove();
          animatingRef.current = false;
        }
      };
      img.onload = () => {
        const trainWidth = Math.round(TRAIN_IMAGE_HEIGHT * (img.naturalWidth / img.naturalHeight));
        const trainMidpoint = visibleWidth * 0.7;
        const trainTop = groupRect.top - containerRect.top - TRAIN_IMAGE_HEIGHT + 20;

        const container = document.createElement('div');
        container.style.cssText = `
          position: absolute;
          height: ${TRAIN_IMAGE_HEIGHT}px;
          width: ${trainWidth}px;
          top: ${trainTop}px;
          left: ${scrollLeft - trainWidth}px;
          pointer-events: none;
          z-index: 9999;
          will-change: transform;
          --train-midpoint: ${trainMidpoint}px;
          --train-distance: ${visibleWidth + trainWidth * 2}px;
          animation: trainMoveAcrossWerklijn 8s ease-in-out forwards;
        `;

        img.style.cssText = `width: 100%; height: 100%;`;
        container.appendChild(img);
        mwdContainer.appendChild(container);
        container.addEventListener('animationend', (ev) => {
          if (ev.target === container) {
            container.remove();
            animatingRef.current = false;
          }
        });

        // Spawn passengers when train stops (40% of 8s = 3.2s)
        const stopTimeMs = 8 * 0.4 * 1000;
        const trainScreenLeft = scrollLeft - trainWidth + trainMidpoint;

        setTimeout(() => {
          PASSENGERS.forEach(({ emoji, xPercent, delay }) => {
            setTimeout(() => {
              const wanderX = (secureRandom() - 0.5) * 140;
              const wanderY = 10 + secureRandom() * 40;

              const person = document.createElement('div');
              person.textContent = emoji;
              person.style.cssText = `
                position: absolute;
                left: ${trainScreenLeft + (xPercent / 100) * trainWidth}px;
                top: ${trainTop + TRAIN_IMAGE_HEIGHT - 4}px;
                font-size: 14px;
                line-height: 1;
                pointer-events: none;
                z-index: 9999;
                --wander-x: ${wanderX}px;
                --wander-y: ${wanderY}px;
                animation: trainPassengerWander 3.5s ease-out forwards;
              `;
              mwdContainer.appendChild(person);
              person.addEventListener('animationend', () => person.remove());
            }, delay * 1000);
          });
        }, stopTimeMs);
      };
    };

    globalThis.addEventListener('trainEasterEgg', handler);
    return () => globalThis.removeEventListener('trainEasterEgg', handler);
  }, []);

  return (
    <style>
      {`
        @keyframes trainMoveAcrossWerklijn {
          0% { transform: translate3d(0, 0, 0); }
          40% { transform: translate3d(var(--train-midpoint), 0, 0); }
          60% { transform: translate3d(var(--train-midpoint), 0, 0); }
          100% { transform: translate3d(var(--train-distance), 0, 0); }
        }
        @keyframes trainPassengerWander {
          0% { opacity: 1; transform: translate(0, 0); }
          15% { opacity: 1; transform: translate(0, 14px); }
          75% { opacity: 1; transform: translate(var(--wander-x), calc(14px + var(--wander-y))); }
          100% { opacity: 0; transform: translate(var(--wander-x), calc(14px + var(--wander-y))); }
        }
      `}
    </style>
  );
}
